import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttendanceSummary } from '@tailoring-crm/shared';
import { formatNumber } from '../../lib/format.js';

/**
 * The four numbers an owner glances at without reading.
 *
 * EVERY ONE OF THEM COMES FROM `summary`, which the server counts over the whole day. Nothing
 * here counts or filters the rows in the table — a shop with forty staff looking at page 2 of 25
 * must still be told it has forty, and a tile that agreed with the page under it would say
 * "15 staff" and be wrong in the one place nobody checks.
 */
export function AttendanceSummaryTiles({ summary }: { summary: AttendanceSummary }): ReactElement {
  const { t } = useTranslation();

  const tiles: readonly { key: string; label: string; value: number }[] = [
    { key: 'totalStaff', label: t('attendance.tile.totalStaff'), value: summary.totalStaff },
    { key: 'completed', label: t('attendance.tile.completed'), value: summary.completed },
    { key: 'working', label: t('attendance.tile.working'), value: summary.working },
    { key: 'onLeave', label: t('attendance.tile.onLeave'), value: summary.onLeave },
  ];

  return (
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          data-testid="attendance-tile"
          className="rounded-lg border border-border p-3"
        >
          <dt className="text-xs text-muted-foreground">{tile.label}</dt>
          <dd className="text-xl font-semibold tabular-nums">{formatNumber(tile.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default AttendanceSummaryTiles;
